<script setup>
    import { ref, inject, watch } from 'vue';
    import { debounce } from 'lodash';

    import SubmitButtonLargeComponent from '@buttons/SubmitButtonLargeComponent.vue';
    import { showSuccessToast, showErrorToast } from '@utils/Toast';
    import { EnvelopeIcon, LockClosedIcon } from '@heroicons/vue/20/solid';
    import MainSiteLogo from '../Includes/menu/MainSiteLogo.vue';
    import {handleErrorResponse} from '@utils/errorHandler';

    const props = defineProps({
        csrf: String,
        route: String,
        routeSuccess: String,
        routeForgotPassword: String,
        routeRegister: String,
    });

    // Inject Axios
    const axios = inject('axios');

    const LABEL_TEXT_ACTIVATE = 'Within minutes you\'ll be able to fill your\n' +
        '"education and research productivity reports online"\n' +
        'and "Activate account"';

    const passwordRegex = /^(?=\S*$)(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*\-]).{10,20}$/;

    //Handle with form
    const email = ref('');
    const password = ref('');
    const isFormValid = ref(false);

    const errors = ref({
        email: null,
        password: null,
    });


    const delayedRedirection = debounce(() => {
        window.location.href = props.routeSuccess;
    }, 1000);

    const validateForm = () => {
        // Check the validity of the input fields
        const isEmailValid = email.value !== '';
        const isPasswordValid = password.value !== '' && passwordRegex.test(password.value);

        errors.value.email = !isEmailValid ? 'Email address is required' : null;
        errors.value.password = !isPasswordValid ? 'Invalid password' : null;

        isFormValid.value = isEmailValid && isPasswordValid;
    };

    const submitForm = async () => {
        validateForm();
        if(!isFormValid.value) {
            // showErrorToast('Invalid form values');
            return false;
        }
        //Prepare the data to be sent
        const formData = {
          email: email.value,
          password: password.value,
        };

        try {
            //Send a POST request to the server
            const response = await axios.post(props.route, formData, {
                headers: {
                    'X-CSRF-TOKEN': props.csrf // Include CSRF token here
                }
            });
            //Handle response here
            if(response.status === 200) {
                const data = response.data;
                if(data.success === 1) {
                    showSuccessToast(data.message);
                    //Save token to local storage
                    localStorage.setItem('auth_token', data.token);

                    delayedRedirection();
                } else {
                    showErrorToast(data.message);
                }
            } else {
                if(response.message) {
                    showErrorToast(response.message);
                }
            }

        } catch (error) {
            showErrorToast(handleErrorResponse(error));
            console.error('Submission error', error);
        }
    };

</script>

<template>
    <div>
        <div class="text-center container max-w-100 md:w-100 bg-white rounded-xl mb-8">
            <div class="wrap-form mb-2 lg:mb-4">
                <form :action="route" id="frmClientLogin" method="post"
                      :data-routesucces="routeSuccess"
                      @submit.prevent="submitForm"
                >
                    <div class="flex flex-col justify-between py-6 mb-6 md:mb-0">
                        <div>
                            <div class="font-bold text-3xl-plus mb-6 text-center leading-tight">Login</div>

                            <div class="form-row">
                                <div class="relative">
                                    <div
                                        class="absolute top-0 left-0 h-full w-10 z-20 flex items-center justify-center">
                                        <envelope-icon class="w-6 text-gray-2"></envelope-icon>
                                    </div>
                                    <input type="email"
                                           v-model="email"
                                           name="email"
                                           class="form-control z-10 px-8"
                                           placeholder="Enter your email address"
                                           autocomplete="off"
                                    ></div>
                                <div v-if="errors.email" class="py-2 text-red text-left" v-text="errors.email"></div>
                            </div>
                            <div class="form-row">
                                <div class="relative">
                                    <div
                                        class="absolute top-0 left-0 h-full w-10 z-20 flex items-center justify-center">
                                        <lock-closed-icon class="w-6 text-gray-2"></lock-closed-icon>
                                    </div>
                                    <input type="password"
                                           v-model="password"
                                           name="password"
                                           class="form-control z-10 placeholder-gray-500"
                                           placeholder="Password"
                                           autocomplete="off"
                                    ></div>
                                <div v-if="errors.password" class="py-2 text-red text-left" v-text="errors.password"></div>
                            </div>
                        </div>
                        <div class="flex items-center justify-end">
                            <div class="py-3 text-right text-dark-gray">
                                <a class="hover:underline" :href="routeForgotPassword">Forgot Password?</a>
                            </div>

                        </div>
                        <div>
                            <SubmitButtonLargeComponent
                                title="Login"
                                btn-class="btn-green-5 w-full !py-4"
                                text-size="text-base"
                            ></SubmitButtonLargeComponent>
                        </div>
                    </div>
                </form>
            </div>
            <div class="text-dark text-xl">
                Don't have an account, <a :href="routeRegister" class="underline">sign up</a> now!
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.form {
    &-row {
        @apply mb-4;
    }
    &-control {
        @apply bg-white py-3 ps-10;
        &::placeholder {
            @apply text-gray-2;
        }
    }
}
</style>
